import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link, useFetcher } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { authAPI, APIError } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { LanguageSwitcher } from "../../components/common/LanguageSwitcher";
import "./auth.css";

type VerifyStatus = "loading" | "success" | "error" | "prompt";

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refetchUser, user } = useAuth();
  const [status, setStatus] = useState<VerifyStatus>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const fetcher = useFetcher();
  const verificationAttemptedRef = useRef(false);

  const token = searchParams.get("token");
  const justRegistered = searchParams.get("registered") === "true";
  const isResending = fetcher.state === "submitting";

  const verifyEmail = useCallback(
    async (verificationToken: string) => {
      try {
        setStatus("loading");
        await authAPI.verifyEmail({ token: verificationToken });
        setStatus("success");
        toast.success(t("auth.verifyEmail.success"));
        await refetchUser();
        setTimeout(() => navigate("/dashboard"), 2000);
      } catch (error) {
        setStatus("error");
        if (error instanceof APIError) {
          const message =
            error.statusCode === 400
              ? t(
                  "auth.verifyEmail.invalidToken",
                  "Invalid or expired verification token. Please request a new one.",
                )
              : error.message || t("auth.verifyEmail.error");
          setErrorMessage(message);
          toast.error(error.message || t("auth.verifyEmail.error"));
        } else {
          setErrorMessage(t("errors.generic"));
          toast.error(t("errors.generic"));
        }
      }
    },
    [navigate, refetchUser, t],
  );

  useEffect(() => {
    if (verificationAttemptedRef.current) return;

    if (token) {
      verificationAttemptedRef.current = true;
      verifyEmail(token);
      return;
    }

    // No token in the URL: this is the normal post-signup state. Don't present it as an error.
    setStatus("prompt");
    setErrorMessage(
      t(
        "auth.verifyEmail.noToken",
        "No verification token found. Please check your email for the verification link.",
      ),
    );
  }, [token, verifyEmail, t]);

  useEffect(() => {
    if (fetcher.data?.success) {
      toast.success(t("auth.verifyEmail.resendSuccess"));
    } else if (fetcher.data?.error) {
      toast.error(fetcher.data.error);
      if (fetcher.data.redirect) {
        navigate(fetcher.data.redirect);
      }
    }
  }, [fetcher.data, navigate, t]);

  return (
    <div className="auth-container">
      <div className="auth-language-switcher">
        <LanguageSwitcher variant="standalone" />
      </div>
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t("auth.verifyEmail.title")}</h1>
        </div>

        {status === "loading" && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{t("auth.verifyEmail.verifying")}</p>
          </div>
        )}

        {status === "success" && (
          <div>
            <div className="success-message">
              <strong>{t("common.success")}!</strong>{" "}
              {t("auth.verifyEmail.successMessage", "Your email has been verified.")}
            </div>
            <p className="redirect-message">
              {t("auth.verifyEmail.redirecting", "Redirecting you to the dashboard...")}
            </p>
          </div>
        )}

        {(status === "error" || status === "prompt") && (
          <div>
            {status === "error" ? (
              <div className="error-message">
                <strong>{t("auth.verifyEmail.failedTitle", "Verification Failed")}</strong>
                <p className="error-message-text">{errorMessage}</p>
              </div>
            ) : (
              <div className="info-message">
                <strong>
                  {justRegistered
                    ? t("auth.verifyEmail.postSignupTitle", "Almost there")
                    : t("auth.verifyEmail.title")}
                </strong>
                <p>
                  {t(
                    "auth.verifyEmail.checkInbox",
                    "Please check your email inbox for the verification link. If you didn't receive it, you can request a new verification email.",
                  )}
                </p>
              </div>
            )}

            <div className="action-buttons">
              {user ? (
                <fetcher.Form method="post" action="/api/auth/resend-verification-action">
                  <button
                    type="submit"
                    className="btn-secondary"
                    disabled={isResending}
                    style={{ width: "100%" }}
                  >
                    {isResending ? (
                      <>
                        <span className="loading-spinner"></span> {t("auth.verifyEmail.resending")}
                      </>
                    ) : (
                      t("auth.verifyEmail.resendButton")
                    )}
                  </button>
                </fetcher.Form>
              ) : (
                <div className="info-message">
                  <p>
                    {t(
                      "auth.verifyEmail.loginToResend",
                      "Please log in to resend your verification email.",
                    )}
                  </p>
                </div>
              )}

              <Link to="/login" className="btn-secondary btn-secondary-link">
                {t("auth.forgotPassword.backToLogin")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
